import React from "react";
import { BaseAnnouncerPack } from "./BaseAnnouncerPack";
import { MessageAnimationHandler, MessageAnimationContext } from "../../../../client/components/MultiPlayer/messageAnimations";
import MessageAnimation from "../../../../client/components/MultiPlayer/animations/MessageAnimation";
import { MESSAGE_VARIANT_COLORS } from "../../../../core/constants";

/**
 * Default announcer pack with text-based animations for all event types
 */
export default class DefaultAnnouncerPack extends BaseAnnouncerPack {
  getAnimationHandler(
    eventType: string,
    _context: MessageAnimationContext
  ): MessageAnimationHandler | null {
    switch (eventType) {
      case "frag":
        return this.handleFrag;
      case "roundWin":
        return this.handleRoundWin;
      case "roundLose":
        return this.handleRoundLose;
      case "firstBlood":
        return this.handleFirstBlood;
      default:
        return null;
    }
  }

  private handleFrag = (context: MessageAnimationContext): React.ReactElement => {
    const { message, localConnectionId, gameState, onComplete } = context;

    if (message.type !== "frag") {
      onComplete();
      return <></>;
    }

    // Resolve fragger and fragged players from gameState using numPlayer
    let fragger: { connectionId: string; name: string } | undefined;
    let fragged: { connectionId: string; name: string } | undefined;

    for (const [, player] of gameState.players.entries()) {
      if (player.numPlayer === message.fraggerNumPlayer) {
        fragger = {
          connectionId: player.connectionId,
          name: player.name || "Unknown",
        };
      }
      if (player.numPlayer === message.fraggedNumPlayer) {
        fragged = {
          connectionId: player.connectionId,
          name: player.name || "Unknown",
        };
      }
    }

    if (!fragger || !fragged) {
      console.warn(`[DefaultAnnouncerPack] Could not resolve players: fraggerNumPlayer=${message.fraggerNumPlayer}, fraggedNumPlayer=${message.fraggedNumPlayer}`);
      onComplete();
      return <></>;
    }

    const isLocalPlayer = fragger.connectionId === localConnectionId;
    const playerName = isLocalPlayer ? fragged.name : fragger.name;
    const headerText = isLocalPlayer ? "YOU FRAGGED" : "FRAGGED BY";
    const headerColor = isLocalPlayer ? "#00ff88" : "#ff4444";

    return (
      <MessageAnimation
        text={playerName}
        headerText={headerText}
        headerColor={headerColor}
        headerFontSize="clamp(20px, 4vw, 30px)"
        fontSize="clamp(24px, 6vw, 48px)"
        color="#FFFFFF"
        textShadow="0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.3)"
        onComplete={onComplete}
      />
    );
  };

  private handleRoundWin = (context: MessageAnimationContext): React.ReactElement => {
    const { message, onComplete } = context;

    if (message.type !== "roundWin") {
      onComplete();
      return <></>;
    }

    return (
      <MessageAnimation
        text={`team ${message.winningTeamName} won`}
        headerText="VICTORY"
        headerColor={MESSAGE_VARIANT_COLORS.positive}
        headerFontSize="clamp(20px, 4vw, 30px)"
        fontSize="clamp(24px, 6vw, 48px)"
        color="#FFFFFF"
        textShadow="0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.3)"
        onComplete={onComplete}
      />
    );
  };

  private handleRoundLose = (context: MessageAnimationContext): React.ReactElement => {
    const { message, onComplete } = context;

    if (message.type !== "roundLose") {
      onComplete();
      return <></>;
    }

    return (
      <MessageAnimation
        text={`team ${message.winningTeamName} won`}
        headerText="DEFEATED"
        headerColor={MESSAGE_VARIANT_COLORS.negative}
        headerFontSize="clamp(20px, 4vw, 30px)"
        fontSize="clamp(24px, 6vw, 48px)"
        color="#FFFFFF"
        textShadow="0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.3)"
        onComplete={onComplete}
      />
    );
  };

  private handleFirstBlood = (context: MessageAnimationContext): React.ReactElement => {
    const { message, onComplete } = context;

    if (message.type !== "firstBlood") {
      onComplete();
      return <></>;
    }

    // Resolve fragger player name
    let fraggerName = "Unknown";
    for (const [, player] of context.gameState.players.entries()) {
      if (player.numPlayer === message.fraggerNumPlayer) {
        fraggerName = player.name || "Unknown";
        break;
      }
    }

    return (
      <MessageAnimation
        text={fraggerName}
        headerText="FIRST BLOOD"
        headerColor="#ff0000"
        headerFontSize="clamp(20px, 4vw, 30px)"
        fontSize="clamp(24px, 6vw, 48px)"
        color="#FFFFFF"
        textShadow="0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 0, 0, 0.5)"
        onComplete={onComplete}
      />
    );
  };
}

